import LegalLayout from "@/components/LegalLayout";

function H2({ children }) {
  return (
    <h2
      className="mt-10 font-heading text-2xl md:text-3xl"
      style={{ fontVariationSettings: "'opsz' 64, 'wght' 500", color: "var(--ink)" }}
    >
      {children}
    </h2>
  );
}

function P({ children }) {
  return <p>{children}</p>;
}

export default function Privacy() {
  return (
    <LegalLayout
      eyebrow="i. The promise"
      title="Privacy"
      italic="policy."
      lastUpdated="July 17, 2026"
      testid="privacy-page"
    >
      <P>
        PintaFlow is a project by <strong style={{ color: "var(--ink)" }}>Pradeep Munjal</strong>, based in Delhi, India.
        This policy explains, in plain English, what we collect, why, how it&apos;s stored, and your rights over it.
      </P>

      <H2>1. What we collect</H2>
      <P>
        <strong style={{ color: "var(--ink)" }}>Account data.</strong> When you sign in with Google, we receive your name, email, and avatar — only to identify you inside PintaFlow. We do not request access to your Gmail, Drive, or contacts.
      </P>
      <P>
        <strong style={{ color: "var(--ink)" }}>Workspace data.</strong> Ideas, scripts, swipe items, tasks, goals, calendar entries and metrics you create inside PintaFlow are stored in our database and only ever visible to you (and PintaFlow operators for support purposes).
      </P>
      <P>
        <strong style={{ color: "var(--ink)" }}>Guest data.</strong> If you use the "Try the live demo" mode without signing in, we create a temporary anonymous account that automatically expires after 24 hours.
      </P>
      <P>
        <strong style={{ color: "var(--ink)" }}>YouTube integration data.</strong> When you optionally connect your YouTube channel, we request <em>read-only</em> access to your channel&apos;s public video list, video stats (views, likes, comments) and channel metadata. We do <strong style={{ color: "var(--ink)" }}>not</strong> post on your behalf, edit your videos, access YouTube Studio, or read your emails. The Google OAuth refresh token is stored encrypted-at-rest and used only for the 12-hour background sync. You can disconnect any time from Settings → Integrations, which revokes our access immediately.
      </P>
      <P>
        <strong style={{ color: "var(--ink)" }}>Usage analytics.</strong> We use PostHog (privacy-respecting product analytics) to understand which features are used. We do not collect IP addresses, fingerprint your browser, or sell any data.
      </P>
      <P>
        <strong style={{ color: "var(--ink)" }}>Feedback.</strong> When you submit feedback via the in-app form, we store your message, optional email, and the page you sent it from — to reply and improve the product.
      </P>

      <H2>2. What we do <em style={{ fontStyle: "italic" }}>not</em> collect</H2>
      <P>
        We do not collect your phone number, your social-media followers, your published video content, or any data outside the PintaFlow application. We do not run ad-targeting scripts.
      </P>

      <H2>3. Third-party services</H2>
      <P>
        We use a small set of trusted services to run PintaFlow:
      </P>
      <ul className="list-disc pl-6 space-y-2">
        <li>Google OAuth &amp; YouTube Data API — sign-in and optional YouTube channel sync (read-only).</li>
        <li>MongoDB Atlas — database hosting.</li>
        <li>OpenAI / Anthropic / Google AI APIs — when you ask the AI Co-Pilot to write a script, your prompt is sent to the model provider and is governed by that provider&apos;s policy. We do not retain training data on these models.</li>
        <li>PostHog — anonymous product analytics (loaded only if you clicked &quot;Accept all&quot; on the cookie banner).</li>
        <li>Razorpay — payment processing (only when paid plans launch).</li>
      </ul>

      <H2>4. Your rights</H2>
      <P>
        You can export your data as CSV at any time from <strong style={{ color: "var(--ink)" }}>Settings → Export</strong>. You can delete your account by emailing us — we will permanently remove your workspace within 7 days.
      </P>

      <H2>5. Children</H2>
      <P>
        PintaFlow is intended for users 16 years and older. If you believe a minor has created an account, please contact us and we will remove it.
      </P>

      <H2>6. Cookies</H2>
      <P>
        We use a single session cookie to keep you signed in, and PostHog uses a session cookie + localStorage for analytics. No advertising cookies.
      </P>

      <H2>7. Changes</H2>
      <P>
        If we update this policy, we will email signed-in users and update the &quot;Last updated&quot; date above. Material changes will be highlighted in-app.
      </P>

      <H2>8. Contact</H2>
      <P>
        Email <a href="mailto:hello@pintaflow.com" style={{ color: "var(--seal)" }}>hello@pintaflow.com</a> for any privacy question. You can also use the in-app feedback button.
      </P>
    </LegalLayout>
  );
}
